import {
  getIndividualSelectOptions,
  getNestedValue,
  getStateOptions,
  setNestedValue,
} from '@/components/tax-form/formFieldHelpers';
import { FormSectionCard } from '@/components/tax-form/FormSectionCard';
import {
  INDIVIDUAL_FIELDS,
  INDIVIDUAL_SECTION_KEYS,
  INDIVIDUAL_SECTIONS,
  type FieldMeta,
  type IndividualSectionKey,
} from '@/components/tax-form/individualFormConfig';
import { ResidencyDetailsSection } from '@/components/tax-form/ResidencyDetailsSection';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerInput } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PERSON_TYPE } from '@/constants/formConstants';
import type { IndividualFormData, PersonalInfoItem, ResidencyDetailItem } from '@/types/form';
import { Plus, Trash2 } from 'lucide-react';

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  required,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}{required ? ' *' : ''}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function renderField(
  fieldMeta: FieldMeta,
  value: unknown,
  onChange: (v: unknown) => void,
  optionsGetter: (key: string) => { value: string; label: string }[]
) {
  const { key, label, placeholder, type = 'text', fullWidth, optionsKey, required, dateFormat } = fieldMeta;
  const val = getNestedValue(value, key);
  const strVal = val != null && typeof val !== 'boolean' ? String(val) : '';
  const numVal = typeof val === 'number' ? val : '';
  const boolVal = !!val;

  const className = fullWidth ? 'space-y-2 sm:col-span-2' : 'space-y-2';
  const labelContent = `${label}${required ? ' *' : ''}`;

  if (type === 'checkbox') {
    return (
      <div key={key} className={className}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={key}
            checked={boolVal}
            onCheckedChange={(c) => onChange(setNestedValue(value, key, !!c))}
          />
          <Label htmlFor={key}>{labelContent}</Label>
        </div>
      </div>
    );
  }

  if (type === 'select') {
    const options = optionsKey ? optionsGetter(optionsKey) : [];
    return (
      <div key={key} className={className}>
        <SelectField
          label={label}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          options={options}
          placeholder={placeholder}
          required={required}
        />
      </div>
    );
  }

  if (type === 'stateSelect') {
    const options = getStateOptions();
    return (
      <div key={key} className={className}>
        <SelectField
          label={label}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          options={options}
          placeholder={placeholder}
          required={required}
        />
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div key={key} className={className}>
        <DatePickerInput
          label={labelContent}
          value={strVal}
          onChange={(v) => onChange(setNestedValue(value, key, v))}
          placeholder={placeholder ?? 'MM/DD/YYYY'}
          required={required}
          fullWidth={fullWidth}
          monthYearOnly={dateFormat === 'monthYear'}
        />
      </div>
    );
  }

  const inputType = type === 'number' ? 'number' : type === 'email' ? 'email' : 'text';
  return (
    <div key={key} className={className}>
      <Label>{labelContent}</Label>
      <Input
        type={inputType}
        value={type === 'number' ? numVal : strVal}
        onChange={(e) => {
          const v = type === 'number' ? (e.target.value ? Number(e.target.value) : undefined) : e.target.value;
          onChange(setNestedValue(value, key, v));
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

function renderSection(
  sectionKey: IndividualSectionKey,
  value: unknown,
  onChange: (v: unknown) => void
) {
  const sectionMeta = INDIVIDUAL_SECTIONS.find((s) => s.key === sectionKey);
  const fields = INDIVIDUAL_FIELDS[sectionKey];
  if (!sectionMeta || !fields) return null;

  const optionsGetter = getIndividualSelectOptions;

  return (
    <FormSectionCard
      key={sectionKey}
      title={sectionMeta.title}
      description={sectionMeta.description}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((field) =>
          renderField(field, value, onChange, optionsGetter)
        )}
      </div>
    </FormSectionCard>
  );
}

/** Normalize personalInformation: [taxpayer], optional [spouse], then [dependents]. */
function normalizePersonalList(
  list: PersonalInfoItem[] | undefined
): PersonalInfoItem[] {
  const arr = Array.isArray(list) && list.length > 0 ? [...list] : [];
  if (arr.length === 0) {
    return [{ personType: PERSON_TYPE.TAXPAYER } as PersonalInfoItem];
  }
  const taxpayer = arr.find((p) => p.personType === PERSON_TYPE.TAXPAYER) ?? { ...arr[0], personType: PERSON_TYPE.TAXPAYER };
  const spouse = arr.find((p) => p.personType === PERSON_TYPE.SPOUSE);
  const dependents = arr.filter((p) => p.personType === PERSON_TYPE.DEPENDENT);
  const out: PersonalInfoItem[] = [
    { ...taxpayer, personType: PERSON_TYPE.TAXPAYER } as PersonalInfoItem,
    ...(spouse ? [{ ...spouse, personType: PERSON_TYPE.SPOUSE } as PersonalInfoItem] : []),
    ...dependents.map((d) => ({ ...d, personType: PERSON_TYPE.DEPENDENT } as PersonalInfoItem)),
  ];
  return out;
}

function getPersonalLabel(index: number, list: PersonalInfoItem[], person: PersonalInfoItem): string {
  if (person.personType === PERSON_TYPE.TAXPAYER) return 'Taxpayer';
  if (person.personType === PERSON_TYPE.SPOUSE) return 'Spouse';
  const dependentIndex = list.slice(0, index).filter((p) => p.personType === PERSON_TYPE.DEPENDENT).length + 1;
  return `Dependent ${dependentIndex}`;
}

function hasSpouse(list: PersonalInfoItem[]): boolean {
  return list.some((p) => p.personType === PERSON_TYPE.SPOUSE);
}

interface ArraySectionItem {
  personType?: string;
  [key: string]: unknown;
}

/** Ensure at least one entry (taxpayer) for array sections. */
function normalizeArraySectionList(list: ArraySectionItem[] | undefined): ArraySectionItem[] {
  const arr = Array.isArray(list) && list.length > 0 ? [...list] : [];
  if (arr.length === 0) {
    return [{ personType: PERSON_TYPE.TAXPAYER }];
  }
  const hasTaxpayer = arr.some((p) => p.personType === PERSON_TYPE.TAXPAYER);
  if (!hasTaxpayer && arr[0]) {
    (arr[0] as ArraySectionItem).personType = PERSON_TYPE.TAXPAYER;
  }
  return arr;
}

function getArraySectionPersonLabel(list: ArraySectionItem[], index: number): string {
  const item = list[index];
  const pt = item?.personType;
  if (pt === PERSON_TYPE.TAXPAYER) return 'Taxpayer';
  if (pt === PERSON_TYPE.SPOUSE) return 'Spouse';
  const dependentIndex = list.slice(0, index + 1).filter((p) => p.personType === PERSON_TYPE.DEPENDENT).length;
  return `Dependent ${dependentIndex}`;
}

function arraySectionHasSpouse(list: ArraySectionItem[]): boolean {
  return list.some((p) => p.personType === PERSON_TYPE.SPOUSE);
}

export interface IndividualFormSectionsProps {
  data: IndividualFormData;
  onChange: (data: IndividualFormData) => void;
  onSaveSection: (key: keyof IndividualFormData, payload: unknown) => Promise<void>;
  savingSection: string | null;
  currentSectionIndex: number;
  onNext: () => void;
  onPrev: () => void;
  isFirstSection: boolean;
  isLastSection: boolean;
}

export function IndividualFormSections({
  data,
  onChange,
  currentSectionIndex,
}: IndividualFormSectionsProps) {
  const set = <K extends keyof IndividualFormData>(key: K, value: IndividualFormData[K]) => {
    onChange({ ...data, [key]: value });
  };

  const sectionKey = INDIVIDUAL_SECTION_KEYS[currentSectionIndex];
  if (!sectionKey) return null;

  const value = data[sectionKey];
  // if (value === undefined) return null;

  // Use defaults when section data hasn't been loaded yet so fields still render
  // const rawValue = data[sectionKey];
  // const value =
  //   rawValue !== undefined
  //     ? rawValue
  //     : sectionKey === 'filingStatus'
  //       ? undefined
  //       : sectionKey === 'personalInformation' ||
  //           sectionKey === 'residencyDetails' ||
  //           sectionKey === 'healthCoverageInformation' ||
  //           sectionKey === 'employerDetails'
  //         ? []
  //         : {};

  if (sectionKey === 'personalInformation') {
    const list = normalizePersonalList(value as PersonalInfoItem[]);
    const updateList = (newList: PersonalInfoItem[]) => {
      onChange({ ...data, personalInformation: newList });
    };
    const addSpouse = () => {
      const spouseEntry = { personType: PERSON_TYPE.SPOUSE } as PersonalInfoItem;
      if (list.some((p) => p.personType === PERSON_TYPE.SPOUSE)) return;
      const taxpayer = list.find((p) => p.personType === PERSON_TYPE.TAXPAYER);
      const dependents = list.filter((p) => p.personType === PERSON_TYPE.DEPENDENT);
      updateList([taxpayer!, spouseEntry, ...dependents]);
    };
    const addDependent = () => {
      updateList([...list, { personType: PERSON_TYPE.DEPENDENT } as PersonalInfoItem]);
    };
    const removePerson = (index: number) => {
      const next = list.filter((_, i) => i !== index);
      updateList(next);
    };
    const sectionMeta = INDIVIDUAL_SECTIONS.find((s) => s.key === sectionKey);
    const baseFields = (INDIVIDUAL_FIELDS[sectionKey] ?? []).filter((f) => f.key !== 'personType');
    if (!sectionMeta) return null;
    const optionsGetter = getIndividualSelectOptions;

    return (
      <FormSectionCard
        title={sectionMeta.title}
        description={sectionMeta.description}
      >
        <div className="space-y-6">
          {list.map((person, index) => (
            <div key={index} className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">{getPersonalLabel(index, list, person)}</span>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removePerson(index)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {baseFields
                  .filter(
                    (field) =>
                      !field.showWhenPersonType ||
                      (person.personType && field.showWhenPersonType.includes(person.personType))
                  )
                  .map((field) =>
                    renderField(
                      field,
                      person,
                      (v) => {
                        const next = [...list];
                        next[index] = v as PersonalInfoItem;
                        updateList(next);
                      },
                      optionsGetter
                    )
                  )}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {!hasSpouse(list) && (
              <Button type="button" variant="outline" size="sm" onClick={addSpouse}>
                <Plus className="h-4 w-4 mr-2" />
                Add spouse
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={addDependent}>
              <Plus className="h-4 w-4 mr-2" />
              Add dependent
            </Button>
          </div>
        </div>
      </FormSectionCard>
    );
  }

  if (sectionKey === 'residencyDetails') {
    return (
      <ResidencyDetailsSection
        residencyDetails={value as ResidencyDetailItem[] | undefined}
        personalInformation={data.personalInformation}
        onChange={(residencyDetails) => onChange({ ...data, residencyDetails })}
      />
    );
  }

  if (
    sectionKey === 'healthCoverageInformation' ||
    sectionKey === 'employerDetails' ||
    sectionKey === 'incomeDetails' ||
    sectionKey === 'deductionsAndExpenses'
  ) {
    const rawList = (Array.isArray(value) ? value : []) as ArraySectionItem[];
    const list = normalizeArraySectionList(rawList);
    const updateList = (newList: ArraySectionItem[]) => {
      onChange({ ...data, [sectionKey]: newList });
    };
    const updateItem = (index: number, itemValue: unknown) => {
      const next = [...list];
      next[index] = itemValue as ArraySectionItem;
      updateList(next);
    };
    const addSpouseEntry = () => {
      if (arraySectionHasSpouse(list)) return;
      updateList([...list, { personType: PERSON_TYPE.SPOUSE }]);
    };
    const addDependentEntry = () => {
      updateList([...list, { personType: PERSON_TYPE.DEPENDENT }]);
    };
    const removeEntry = (index: number) => {
      const next = list.filter((_, i) => i !== index);
      updateList(next);
    };

    const sectionMeta = INDIVIDUAL_SECTIONS.find((s) => s.key === sectionKey);
    const fields = INDIVIDUAL_FIELDS[sectionKey];
    if (!sectionMeta || !fields) return null;
    const optionsGetter = getIndividualSelectOptions;
    const isHealthSection = sectionKey === 'healthCoverageInformation';

    return (
      <FormSectionCard
        key={sectionKey}
        title={sectionMeta.title}
        description={sectionMeta.description}
      >
        <div className="space-y-6">
          {list.map((item, index) => (
            <div key={index} className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {getArraySectionPersonLabel(list, index)}
                </span>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeEntry(index)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.map((field) =>
                  renderField(
                    field,
                    item,
                    (v) => updateItem(index, v),
                    optionsGetter
                  )
                )}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {!arraySectionHasSpouse(list) && (
              <Button type="button" variant="outline" size="sm" onClick={addSpouseEntry}>
                <Plus className="h-4 w-4 mr-2" />
                Add spouse
              </Button>
            )}
            {isHealthSection && (
              <Button type="button" variant="outline" size="sm" onClick={addDependentEntry}>
                <Plus className="h-4 w-4 mr-2" />
                Add dependent
              </Button>
            )}
          </div>
        </div>
      </FormSectionCard>
    );
  }

  const isScalarSection = sectionKey === 'filingStatus';
  const valueForRender: unknown = isScalarSection ? { filingStatus: value } : value;
  const onChangeSection = (v: unknown) => {
    if (isScalarSection) {
      set('filingStatus', (v as { filingStatus?: IndividualFormData['filingStatus'] }).filingStatus);
    } else {
      onChange({ ...data, [sectionKey]: v });
    }
  };
  return renderSection(
    sectionKey,
    valueForRender,
    onChangeSection
  );
}
